/**
 * SL-11 — DIMENSION `ACAD`/`DSTYLE` arrowhead-block handle resolution (export).
 *
 * A DIMENSION's per-entity DIMSTYLE overrides ride `ACAD` XDATA as a `DSTYLE`
 * block. The arrowhead-block DIMVARs — `dimblk` (342), `dimblk1` (343),
 * `dimblk2` (344) — are HANDLE-typed in that block: per the Autodesk DXF R2018
 * DSTYLE appendix and `ezdxf` (`get_xcode_for` maps every DIMSTYLE code in
 * 320-369 → XDATA `1005` soft-pointer handle; only R12 code 5 stays `1000`),
 * their value is a soft-pointer handle to the arrowhead BLOCK_RECORD, NOT the
 * block name. `buildDimensionStyleOverridesXData` emits the model's block NAME
 * (or `""` for the default CLOSEDFILLED arrow) under a `1000 <name>` STRING
 * placeholder — never a bare `1005 <name>`; THIS pass — run at export next to
 * the MLEADER block-content stamp, where the BLOCK_RECORD name↔handle population
 * is known — is the SOLE producer of the final `1005 <handle>`, resolving that
 * placeholder to a real handle so AutoCAD/ezdxf keep the override (F6). A
 * bypassed export therefore emits a valid (silently-dropped) `1000` string, never
 * a structurally-invalid `1005` naming value.
 *
 * Empirically (ezdxf 1.4.3 `get_acad_dstyle`): a `1005` that resolves to a live
 * BLOCK_RECORD reads the override back correctly; the special null handle `0`
 * reads as the default CLOSEDFILLED arrow (name `""`); a `1005` pointing at a
 * missing block — or a `1000 <name>` — is SILENTLY DROPPED. So a faithful
 * export must point 1005 at a real block record (or `0`) — never a name/dangling
 * handle.
 *
 * ── Complete arrowhead-value resolution matrix (case-insensitive; DXF hex
 *    handles and block names are both case-insensitive). Source of truth: ezdxf
 *    1.4.3 `Dimension.dim_style_attr_names_to_handles` / `set_arrow_handle`
 *    (`entities/dimension.py`) + `render/arrows.py` `ARROWS`.
 *
 *    The value is disambiguated by PROVENANCE, carried in the DSTYLE VALUE group
 *    CODE (SL-11 round-3 #3): `1005` = an IMPORTED soft-pointer HANDLE (the
 *    `dimblk*Handle` model twin, emitted by `buildDimensionStyleOverridesXData`),
 *    `1000` = an AUTHORED block NAME placeholder. This resolves the `A1`-handle
 *    vs `A1`-named-block ambiguity by SOURCE, not by a fragile name-first ordering:
 *
 *   • V === "" / "." / "_CLOSEDFILLED" / "0" (either code) → `0`. The default
 *     CLOSEDFILLED arrow is the ezdxf null handle `0` (`set_arrow_handle`:
 *     `_CLOSEDFILLED` → "0"); AutoCAD's DIMBLK "." sentinel + the `_CLOSEDFILLED`
 *     block name + an imported `1005 0` all denote that same default.
 *   • IMPORTED HANDLE (code `1005`): kept VERBATIM when it points at a live
 *     BLOCK_RECORD (`handleToName` has it) — byte-faithful passthrough for an
 *     imported dim's `1005 <handle>` (already record-remap-followed by the
 *     conformant skeleton, since `dimblk*Handle` rides `HANDLE_FIELD_CLASS`).
 *     It is NEVER re-resolved to a block that merely SHARES its spelling as a
 *     NAME (#3). A dangling imported handle (no live record) is DROPPED.
 *   • AUTHORED NAME (code `1000`) matching a block NAME defined in the doc →
 *     rewritten to that record's handle (F8: a block whose NAME looks like some
 *     OTHER record's HANDLE resolves to ITS OWN record). A handle-LESS matching
 *     record gets a handle MINTED onto it (F5); a matching BLOCK with no record
 *     is ADOPTED — a record is minted for it (#1) — never a synthesized duplicate.
 *   • AUTHORED NAME that is a built-in AutoCAD arrow (ezdxf `ARROWS.__acad__`,
 *     e.g. `DOT` / `_DOT` / `OPEN30` / `_ARCHTICK`) with NO matching block →
 *     resolve to the arrow's canonical `_<NAME>` BLOCK_RECORD: reuse / adopt an
 *     already-present record/block (minting a handle if handle-less, F5), else
 *     SYNTHESIZE record + BLOCK on demand (mirrors ezdxf `set_arrow_handle` →
 *     `ARROWS.create_block`, and AutoCAD auto-creating the arrow block on use).
 *     NEVER dropped.
 *   • otherwise (an authored custom name with no BLOCK_RECORD / BLOCK and not a
 *     built-in arrow) → the whole `1070 <code>` + value pair is DROPPED. Never
 *     fabricate a dangling `1005` (AutoCAD/ezdxf drop it anyway); the style's own
 *     arrow is used, exactly as a dropped override behaves.
 *
 * The DSTYLE XDATA is parsed as its actual PAIR structure —
 * `1000 "DSTYLE" 1002 "{" ( <opener> <value> )* 1002 "}"` — where each opener is
 * `1070 <dimvar-code>` and the very next group is that dimvar's value. A `1070`
 * sitting at a VALUE position (an integer DIMVAR whose value happens to be
 * 342/343/344) is NEVER mistaken for an arrowhead opener; the walk advances
 * opener+value together (mirrors `extractDimensionStyleOverrides` and ezdxf's
 * `take2` over the DSTYLE tags).
 *
 * Mirrors `stampMLeaderBlockContentHandles` (SL-5) — same name↔handle map build
 * from `blockRecords`, same top-level + block-child entity sweep at the export
 * boundary. Source of truth: Autodesk DXF R2018 + ezdxf 1.4.3. AutoCAD MCP is
 * BANNED.
 */
import {
  DIM_ARROWHEAD_BLOCK_CODES,
  type DxfBlockDefinition,
  type DxfBlockRecordEntry,
  type DxfBlocksSection,
  type DxfEntity,
  type DxfGroup,
  type DxfXDataEntry,
} from "@dxf-ai-editor/dxf-io";

// SL-11 round-3 #2 / round-5 — the built-in AutoCAD arrow NAME set AND the
// canonical `_<NAME>` block-record resolver are SHARED with the reference-audit
// sentinel whitelist + the dependency index (`builtinArrowNames.ts` is the ONE
// source of truth) so a strict pre-export audit / a PURGE dependency query can
// never disagree with what this stamp resolves.
import {
  DEFAULT_ARROWHEAD_HANDLE,
  builtinArrowBlockRecordName,
  isDefaultArrowheadValue,
} from "./builtinArrowNames";
// SL-11 F7 (DIM-ARROW-BLOCK-GEOMETRY) — the real dot/tick/oblique/open/closed/box/
// datum/origin/integral SHAPE geometry a synthesized built-in arrow BLOCK carries,
// a faithful port of ezdxf `ARROWS.create_block`. Kept in its own module so this
// resolver does not grow with ~20 arrow-geometry definitions.
import { buildBuiltinArrowBlockEntities } from "./builtinArrowGeometry";

/** The special ezdxf null handle for the default CLOSEDFILLED arrowhead. */
const DEFAULT_CLOSEDFILLED_HANDLE = DEFAULT_ARROWHEAD_HANDLE;

/** A valid DXF handle is a non-empty run of hex digits (case-insensitive). */
function isHexHandle(value: string): boolean {
  return /^[0-9A-Fa-f]+$/.test(value);
}

/**
 * Build the doc-wide, collision-free HEX handle minter shared by BOTH arrowhead
 * export stamps (DIMENSION DSTYLE + MLEADER body-342). Every minted handle is
 * folded into `emittedHandles` so a later mint — this stamp, its sibling stamp
 * (their `emittedHandles` set is shared and both seeds derive from its running
 * max), or the writer's `$HANDSEED` repatch — never collides. SL-11 round-9 F2:
 * ONE minter, no clone (the two stamps carried an identical inline closure).
 */
function makeHandleMinter(
  emittedHandles: Set<string>,
  handleSeedStart: number,
): () => string {
  let handleSeed = handleSeedStart;
  return (): string => {
    handleSeed += 1;
    const hex = handleSeed.toString(16).toUpperCase();
    emittedHandles.add(hex.toLowerCase());
    return hex;
  };
}

/**
 * Mint a doc-wide hex handle ONTO a handle-LESS (or NON-HEX) BLOCK_RECORD, register
 * both name↔handle mappings, and return the new handle. The single minting truth
 * shared by the DIMENSION DSTYLE resolver ({@link resolveExistingRecordByName}) and
 * the MLEADER body-342 stamp ({@link resolveMleaderEntityArrowheadForExport}) so a
 * handle-less / non-hex arrow record is minted IDENTICALLY at both sites (SL-11
 * round-9 F2 — reuse the round-8 F1 minter, never a parallel copy). Registering the
 * new handle in `handleToName` too lets a LATER dim/mleader that references the same
 * record (by name OR the now-minted handle) dedup to this one record.
 */
function mintHandleOntoRecord(
  record: DxfBlockRecordEntry,
  nameKey: string,
  nameToHandle: Map<string, string>,
  handleToName: Map<string, string>,
  mintHandle: () => string,
): string {
  const minted = mintHandle();
  record.handle = minted;
  nameToHandle.set(nameKey, minted);
  handleToName.set(minted.toLowerCase(), record.name);
  return minted;
}

/**
 * Wire an EXISTING BLOCK definition to be owned by BLOCK_RECORD handle
 * `recordHandle`: point its group-330 owner at the record and mint its own
 * group-5 handle when it is handle-less. The writer emits a BLOCK's owner `330`
 * (and handle `5`) SOLELY from `block.ownerHandle` / `block.handle`
 * (`serializeBlockDefinition`), so an un-wired matching block ships unowned /
 * stale even when the DSTYLE points at a valid record. Shared by the no-record
 * ADOPTION path and the handle-less-record path (SL-11 round-4 R11) so both
 * leave the block owned IDENTICALLY (one wiring rule, no drift / duplication).
 */
function wireBlockOwnership(
  block: DxfBlockDefinition,
  recordHandle: string,
  mintHandle: () => string,
): void {
  if (!isHexHandle(block.handle)) block.handle = mintHandle();
  block.ownerHandle = recordHandle;
}

/**
 * Internal synthesis hook: mints a document-wide collision-free handle and wires
 * a synthesized arrow BLOCK_RECORD + its matching BLOCK into the export. Built by
 * {@link stampDimensionDStyleArrowheadHandles} over the mutable collections the
 * export orchestrator threads in.
 */
interface ArrowBlockSynthesisContext {
  mintHandle(): string;
  addArrowBlock(record: DxfBlockRecordEntry, block: DxfBlockDefinition): void;
  /**
   * #1 — adopt an EXISTING BLOCK definition (present in the BLOCKS section but
   * lacking a matching BLOCK_RECORD): mint a BLOCK_RECORD for THAT block (adopting
   * its geometry), wire the block's own group-5 + 330 owner, and return the new
   * record handle — instead of synthesizing a DUPLICATE `_<NAME>` block+record.
   */
  adoptExistingBlock(block: DxfBlockDefinition): string;
}

/**
 * Resolve every DIMENSION's DSTYLE arrowhead-block override (dimblk/dimblk1/
 * dimblk2 — group codes in {@link DIM_ARROWHEAD_BLOCK_CODES}) from a block NAME
 * / preserved handle / built-in arrow to a real BLOCK_RECORD handle (or the null
 * handle `0`), mutating the transport entities' `ACAD` XDATA in place. A built-in
 * arrow with no matching record is synthesized (its `_<NAME>` BLOCK_RECORD is
 * pushed onto `blockRecords`, its BLOCK onto the returned BLOCKS section — created
 * here if the document declared none, minting handles above `handleSeedStart` and
 * folding them into `emittedHandles`); a value that resolves to no live/built-in
 * block record has its override dropped (never fabricate a dangling handle).
 *
 * Runs on BOTH the conformant and the plain (non-conformant) export paths, so it
 * ALSO normalizes the `1000 <name>` placeholders `buildDimensionStyleOverridesXData`
 * emits even when the document declares no block records (F3): the emitted `1005`
 * this pass produces never carries a non-hex / empty value, and no bare
 * `1005 <name>` ever survives to the writer.
 *
 * @returns the BLOCKS section to emit — the passed `blocksSection` unchanged, or a
 *   freshly created one when synthesis needed a home and none existed (null only
 *   when there was no section and nothing was synthesized).
 */
export function stampDimensionDStyleArrowheadHandles(
  entities: DxfEntity[],
  blockRecords: DxfBlockRecordEntry[],
  emittedHandles: Set<string>,
  handleSeedStart: number,
  blocksSection: DxfBlocksSection | null,
): DxfBlocksSection | null {
  const handleToName = new Map<string, string>();
  const nameToHandle = new Map<string, string>();
  // F5 — name → the actual BLOCK_RECORD OBJECT (first write wins), covering
  // records that carry NO group-5 handle yet (from-scratch / non-conformant
  // path). When an override names such a record, we MINT a handle ONTO it
  // (`resolveExistingRecordByName`) rather than synthesizing a duplicate block
  // or dropping the override — the record already exists, it just lacks a handle.
  const nameToRecord = new Map<string, DxfBlockRecordEntry>();
  for (const rec of blockRecords) {
    if (!rec.name) continue;
    if (!nameToRecord.has(rec.name.toLowerCase())) {
      nameToRecord.set(rec.name.toLowerCase(), rec);
    }
    // SL-11 round-8 F1 — ONLY a HEX handle is a valid DSTYLE `1005` target. A
    // programmatically-built BLOCK_RECORD on the from-scratch / non-conformant path
    // can carry a NON-HEX model handle (a synthetic `model\0…` id), and emitting it
    // under `1005` ships a structurally-invalid handle ezdxf/AutoCAD drop. So a
    // non-hex handle is EXCLUDED from BOTH handle maps: the record then resolves via
    // `nameToRecord` below, where `resolveExistingRecordByName` MINTS a real hex
    // handle onto it (mirroring the handle-LESS record path). A non-hex value under
    // the imported-handle passthrough (`handleToName`) is likewise never accepted →
    // a dangling `1005 <non-hex>` is dropped, never emitted.
    if (!rec.handle || !isHexHandle(rec.handle)) continue;
    handleToName.set(rec.handle.toLowerCase(), rec.name);
    // First write wins so name → handle is deterministic if two records share a
    // name (keeps the earliest BLOCK_RECORD's handle) — mirrors the MLEADER map.
    if (!nameToHandle.has(rec.name.toLowerCase())) {
      nameToHandle.set(rec.name.toLowerCase(), rec.handle);
    }
  }
  // #1 — name → the EXISTING BLOCK definition in the BLOCKS section. When a
  // built-in / named arrow override resolves to a block that already has a BLOCK
  // definition but NO matching BLOCK_RECORD (the non-conformant path, where the
  // conformant-skeleton BLOCK⇄BLOCK_RECORD completeness pass never ran), we ADOPT
  // that block (mint a record for it) rather than SYNTHESIZE a duplicate empty
  // block whose record the DSTYLE would then point at.
  const nameToBlock = new Map<string, DxfBlockDefinition>();
  for (const b of blocksSection?.blocks ?? []) {
    if (b.name && !nameToBlock.has(b.name.toLowerCase())) {
      nameToBlock.set(b.name.toLowerCase(), b);
    }
  }
  // NOTE (F3): do NOT early-return on an empty block-record map. The `1005`
  // placeholders emitted at the build boundary must still be normalized on the
  // non-conformant path (closed-filled → `0`, built-in arrow → synthesized
  // record, unresolvable → dropped) so an invalid non-hex `1005` never ships.

  let section = blocksSection;
  const mintHandle = makeHandleMinter(emittedHandles, handleSeedStart);
  const synthesis: ArrowBlockSynthesisContext = {
    mintHandle,
    addArrowBlock: (record, block) => {
      blockRecords.push(record);
      section ??= { kind: "blocks-section", blocks: [], rawBlockGroups: [] };
      section.blocks.push(block);
    },
    adoptExistingBlock: (block) => {
      const recordHandle = mintHandle();
      const record: DxfBlockRecordEntry = {
        name: block.name,
        handle: recordHandle,
        insertionUnits: 0,
        explodability: 1,
        scalability: 0,
      };
      blockRecords.push(record);
      // Wire the EXISTING block's own group-5 handle + its 330 owner to the new
      // BLOCK_RECORD so the writer emits a valid, owned BLOCK ⇄ BLOCK_RECORD pair
      // (the block keeps its own geometry). Only the RECORD is added — the block
      // is already in the section, so no duplicate BLOCK is pushed.
      wireBlockOwnership(block, recordHandle, mintHandle);
      return recordHandle;
    },
  };

  for (const entity of entities) {
    if (entity.kind !== "supported-entity" || entity.type !== "DIMENSION") {
      continue;
    }
    if (!entity.xdata) continue;
    for (const xentry of entity.xdata) {
      if (xentry.appName !== "ACAD") continue;
      resolveArrowheadHandlesInAcadXData(
        xentry,
        handleToName,
        nameToHandle,
        nameToRecord,
        nameToBlock,
        synthesis,
      );
    }
  }
  return section;
}

/**
 * Rewrite the arrowhead-block value groups inside one `ACAD` XData entry's
 * `groups`, resolving each `1070 <arrowheadCode>` value per the class matrix.
 * Walks the DSTYLE block as its `1000 "DSTYLE" 1002 "{" (opener value)* 1002 "}"`
 * pair structure so an integer DIMVAR whose VALUE is 342/343/344 is never
 * mistaken for an arrowhead opener (F2). Rebuilds the groups list (a resolved
 * value may be dropped, shifting indices). Non-DSTYLE / non-arrowhead groups pass
 * through verbatim.
 */
function resolveArrowheadHandlesInAcadXData(
  xentry: DxfXDataEntry,
  handleToName: Map<string, string>,
  nameToHandle: Map<string, string>,
  nameToRecord: Map<string, DxfBlockRecordEntry>,
  nameToBlock: Map<string, DxfBlockDefinition>,
  synthesis: ArrowBlockSynthesisContext,
): void {
  const src = xentry.groups;
  const out: DxfGroup[] = [];
  let i = 0;
  while (i < src.length) {
    const g = src[i]!;
    out.push(g);
    i += 1;
    if (!(g.code === 1000 && g.value === "DSTYLE")) continue;
    // Enter the DSTYLE sub-list: optional `1002 "{"`, then (opener value) pairs
    // until `1002 "}"`. Mirrors `extractDimensionStyleOverrides` + ezdxf `take2`.
    if (i < src.length && src[i]!.code === 1002 && src[i]!.value === "{") {
      out.push(src[i]!);
      i += 1;
    }
    while (i < src.length) {
      const head = src[i]!;
      if (head.code === 1002 && head.value === "}") {
        out.push(head);
        i += 1;
        break;
      }
      if (head.code !== 1070) {
        // Not an opener (malformed / unexpected) — pass through, advance 1.
        out.push(head);
        i += 1;
        continue;
      }
      // Opener `1070 <dimvar-code>`; the VALUE is the NEXT group, whatever its
      // code. Advance opener+value together (never re-read the value as an opener).
      const opener = head;
      const value = src[i + 1];
      const dimVarCode = Number.parseInt(opener.value, 10);
      if (value !== undefined && DIM_ARROWHEAD_BLOCK_CODES.has(dimVarCode)) {
        const resolved = resolveArrowheadValue(
          value.value,
          value.code,
          handleToName,
          nameToHandle,
          nameToRecord,
          nameToBlock,
          synthesis,
        );
        if (resolved === undefined) {
          // Unresolved → drop the `1070 <code>` opener AND its value (skip both).
          i += 2;
          continue;
        }
        out.push(opener);
        out.push({ code: 1005, value: resolved });
        i += 2;
        continue;
      }
      // Non-arrowhead opener → pass opener + its value verbatim.
      out.push(opener);
      if (value !== undefined) {
        out.push(value);
        i += 2;
      } else {
        i += 1;
      }
    }
  }
  xentry.groups = out;
}

/**
 * Resolve a single arrowhead-override value to a BLOCK_RECORD handle (or the
 * null handle `0`), synthesizing a built-in arrow's block record on demand.
 * Returns `undefined` when the value names no live / built-in block record
 * (caller drops the override). See the module matrix.
 *
 * `sourceCode` is the DXF group code the value arrived under — the PROVENANCE
 * discriminator (SL-11 round-3 #3): `1005` = an IMPORTED soft-pointer HANDLE
 * (from `buildDimensionStyleOverridesXData` emitting the `dimblk*Handle`
 * provenance twin — already record-remap-followed by the conformant skeleton),
 * anything else (`1000`) = an AUTHORED block NAME placeholder. The two paths are
 * DISJOINT: an imported handle is kept verbatim (NEVER re-resolved to a
 * same-spelled block NAME — the `A1`-handle vs `A1`-name ambiguity #3), and an
 * authored name goes through the name / built-in resolution (NEVER treated as a
 * raw handle). Both first normalize the closed-filled default to the null handle.
 */
function resolveArrowheadValue(
  value: string,
  sourceCode: number,
  handleToName: Map<string, string>,
  nameToHandle: Map<string, string>,
  nameToRecord: Map<string, DxfBlockRecordEntry>,
  nameToBlock: Map<string, DxfBlockDefinition>,
  synthesis: ArrowBlockSynthesisContext,
): string | undefined {
  const key = value.toLowerCase();
  // Closed-filled default (empty / "." / "_CLOSEDFILLED" / null handle "0") →
  // the null handle `0`. Applies to BOTH provenance paths (an imported `1005 0`
  // is the closed-filled default; an authored "" / "." / "_CLOSEDFILLED" too).
  if (
    value === "" ||
    key === "." ||
    key === "_closedfilled" ||
    value === DEFAULT_CLOSEDFILLED_HANDLE
  ) {
    return DEFAULT_CLOSEDFILLED_HANDLE;
  }
  // #3 — IMPORTED provenance (`1005`): the value is a soft-pointer HANDLE. Keep it
  // VERBATIM when it points at a live BLOCK_RECORD (already record-remap-followed
  // upstream) — NEVER re-resolve it to a block that merely SHARES its spelling as a
  // NAME (the `A1`-handle vs `A1`-named-block ambiguity). A dangling imported handle
  // (no live record) is DROPPED, exactly as AutoCAD/ezdxf drop it.
  if (sourceCode === 1005) {
    if (handleToName.has(key)) return value;
    return undefined;
  }
  // AUTHORED provenance (`1000` NAME placeholder). F8 — an EXACT block-NAME match
  // wins: a model-authored arrow block whose NAME happens to look like some record's
  // HANDLE resolves to ITS OWN record. The name lookup also mints a handle onto a
  // matching handle-LESS record (F5) or ADOPTS an existing BLOCK with no record (#1).
  const byName = resolveExistingRecordByName(
    value,
    handleToName,
    nameToHandle,
    nameToRecord,
    nameToBlock,
    synthesis,
  );
  if (byName !== undefined) return byName;
  // A built-in AutoCAD arrow name with no matching block record → its canonical
  // `_<NAME>` record: reuse an already-present record / adopt an existing block
  // (minting a handle onto a handle-less record, F5), else synthesize record + block.
  const recordName = builtinArrowBlockRecordName(value);
  if (recordName !== null) {
    const existing = resolveExistingRecordByName(
      recordName,
      handleToName,
      nameToHandle,
      nameToRecord,
      nameToBlock,
      synthesis,
    );
    if (existing !== undefined) return existing;
    return synthesizeArrowBlock(recordName, handleToName, nameToHandle, synthesis);
  }
  // No live / built-in block record → drop (never fabricate a dangling 1005).
  return undefined;
}

/**
 * Resolve a block NAME to a BLOCK_RECORD handle when a record bearing that exact
 * name exists in the document, else `undefined` (no such record).
 *
 * Three cases:
 *   • the record already carries a group-5 handle → return it (via `nameToHandle`);
 *   • the record exists but is handle-LESS (from-scratch / non-conformant path,
 *     F5) → MINT a doc-wide collision-free handle ONTO that existing record and
 *     adopt it, registering the new name↔handle mapping so a later dim naming the
 *     same block dedups to the one record;
 *   • no BLOCK_RECORD by this name but a BLOCK definition IS present in the BLOCKS
 *     section (#1) → ADOPT that block: mint a BLOCK_RECORD for it (wiring the
 *     block's own handle + 330 owner) so the override points at the USER's block
 *     geometry, NOT a synthesized duplicate.
 * This never synthesizes a duplicate and never drops an override that names a
 * block the document actually declares (as a record OR a BLOCK definition).
 */
function resolveExistingRecordByName(
  name: string,
  handleToName: Map<string, string>,
  nameToHandle: Map<string, string>,
  nameToRecord: Map<string, DxfBlockRecordEntry>,
  nameToBlock: Map<string, DxfBlockDefinition>,
  synthesis: ArrowBlockSynthesisContext,
): string | undefined {
  const key = name.toLowerCase();
  const withHandle = nameToHandle.get(key);
  if (withHandle !== undefined) return withHandle;
  const record = nameToRecord.get(key);
  // Guard: any record reachable here carries NO HEX handle (a hex-handle-bearing
  // record would have hit `nameToHandle` above). This is EITHER a handle-LESS
  // record OR — SL-11 round-8 F1 — a record with a NON-HEX handle (a from-scratch
  // `model\0…` synthetic id): both are treated identically, mint a real hex handle
  // ONTO the record so the DSTYLE `1005` target is a valid handle, never a stale
  // non-hex id. Register the new name↔handle so a later dim naming the same block
  // dedups to the one record.
  if (record !== undefined && !(record.handle && isHexHandle(record.handle))) {
    const minted = mintHandleOntoRecord(
      record,
      key,
      nameToHandle,
      handleToName,
      synthesis.mintHandle,
    );
    // R11 — a matching BLOCK definition may CO-EXIST with the handle-less record
    // (both present on the non-conformant / from-scratch path). The stamp reached
    // this branch (record present) so the no-record ADOPTION path never runs and
    // never wires the block; wire it HERE the same way (owner 330 → this record,
    // mint the block's own group-5 if handle-less). Otherwise the DSTYLE points
    // at the owned record while the matching block ships unowned/stale.
    const block = nameToBlock.get(key);
    if (block !== undefined) wireBlockOwnership(block, minted, synthesis.mintHandle);
    return minted;
  }
  // #1 — no BLOCK_RECORD, but a BLOCK definition with this name EXISTS in the
  // section: adopt it (mint a record for THAT block) instead of synthesizing a
  // duplicate. Register the mappings so a later same-name dim dedups to it.
  const block = nameToBlock.get(key);
  if (block !== undefined) {
    const minted = synthesis.adoptExistingBlock(block);
    nameToHandle.set(key, minted);
    handleToName.set(minted.toLowerCase(), block.name);
    return minted;
  }
  return undefined;
}

/**
 * Mint + register a BLOCK_RECORD (and its matching, geometry-free BLOCK) for a
 * built-in arrow whose block is absent, returning the record handle. Updates the
 * local name↔handle maps so a second DIMENSION referencing the same arrow dedups
 * to the one synthesized record. The BLOCK is intentionally empty: ezdxf 1.4.3
 * (the conformance oracle) resolves the override by the record NAME alone
 * (`_OPEN30` → `OPEN30`), audit-clean; the arrow SHAPE geometry is regenerated
 * by the CAD app from its built-in arrow definition. SL-11 F7
 * (DIM-ARROW-BLOCK-GEOMETRY): the block body is now POPULATED with the arrow's real
 * SHAPE geometry — a faithful port of ezdxf `ARROWS.create_block` — via
 * {@link buildBuiltinArrowBlockEntities}, so AutoCAD / ezdxf draw + plot the correct
 * arrowhead (and the synthesized block still audits 0/0 + resolves by NAME). Each
 * child entity gets a doc-wide minted handle and this BLOCK_RECORD as its 330 owner.
 */
function synthesizeArrowBlock(
  recordName: string,
  handleToName: Map<string, string>,
  nameToHandle: Map<string, string>,
  synthesis: ArrowBlockSynthesisContext,
): string {
  const recordHandle = synthesis.mintHandle();
  const blockHandle = synthesis.mintHandle();
  const record: DxfBlockRecordEntry = {
    name: recordName,
    handle: recordHandle,
    insertionUnits: 0,
    explodability: 1,
    scalability: 0,
  };
  const block: DxfBlockDefinition = {
    name: recordName,
    handle: blockHandle,
    ownerHandle: recordHandle,
    layer: "0",
    flags: 0,
    basePoint: { x: 0, y: 0, z: 0 },
    xrefPathName: "",
    description: "",
    // SL-11 F7 — the arrow's real geometry (LINE/CIRCLE/ARC/LWPOLYLINE/SOLID),
    // each child owned by this BLOCK_RECORD (330 = recordHandle) with its own
    // minted group-5 handle folded into the doc-wide census.
    entities: buildBuiltinArrowBlockEntities(recordName, {
      mintHandle: synthesis.mintHandle,
      ownerHandle: recordHandle,
    }),
    endblkGroups: [],
    // T9b — the synthesized block's ENDBLK needs its own group-5 too (R13+;
    // a handle-less ENDBLK is silently re-minted by ezdxf, recovered by AutoCAD).
    endblkHandle: synthesis.mintHandle(),
  };
  synthesis.addArrowBlock(record, block);
  // Register so subsequent dims referencing the same arrow reuse this record.
  nameToHandle.set(recordName.toLowerCase(), recordHandle);
  handleToName.set(recordHandle.toLowerCase(), recordName);
  return recordHandle;
}

/**
 * SL-11 round-8 F2 — resolve a MLEADER ENTITY `arrowheadBlock` (AcDbMLeader body
 * group 342 = arrow_head_handle) to the value the writer must emit VERBATIM.
 *
 * The dxf-io writer emits body-342 straight from `geometry.arrowheadBlock`
 * (`writer.ts`: `if (g.arrowheadBlock) pushGroup(342, g.arrowheadBlock)`), and 342
 * is a soft-pointer HANDLE. So an AUTHORED block NAME (e.g. `MYARROW`) would be
 * written verbatim and, on reopen, read as a HANDLE `MYARROW` → DANGLES. This
 * resolves it to a real handle BEFORE the writer runs. Ordering mirrors
 * `resolveMleaderArrowheadRef` (the dependency/audit resolver) so all sites agree:
 *
 *   - closed-filled / null-handle DEFAULT arrow (`"" / "." / "_CLOSEDFILLED" / "0"`,
 *     the {@link isDefaultArrowheadValue} predicate) → the null handle `"0"` (SL-11
 *     round-9 F1). Body-342 is a soft-pointer HANDLE, so a NON-hex default spelling
 *     (`.` / `_CLOSEDFILLED`) written verbatim would DANGLE on reopen. ezdxf 1.4.3
 *     `entities/mleader.py` (`# arrow_head_handle is None for default arrow 'closed
 *     filled'` → `write_handle_if_exist(342, …)`) omits 342 for the default arrow;
 *     the null handle `"0"` is the equivalent VALID soft-pointer — it reads back as
 *     no-block / the built-in default and audits 0/0 (proven against the ezdxf
 *     readback), never the literal `.`/`_CLOSEDFILLED` naming string.
 *   - an IMPORTED soft-pointer HANDLE that targets a LIVE BLOCK_RECORD → kept
 *     VERBATIM (round-7 handle-first — never re-resolved to a same-spelled NAME);
 *   - an AUTHORED NAME matching a declared BLOCK_RECORD → that record's hex HANDLE
 *     (round-8 — body-342 now points at a real block, resolves on reopen). When the
 *     matching record is handle-LESS / NON-HEX (from-scratch / non-conformant path,
 *     where the conformant skeleton never minted one), a hex handle is MINTED ONTO
 *     that existing record via the SAME minter the DIMENSION `dimblk*` handle-less
 *     path uses (SL-11 round-9 F2), so the arrow resolves instead of being dropped;
 *   - anything else (a custom name with no record, a built-in arrow name with no
 *     backing block — MLEADER does NOT synthesize built-ins, unlike DIMENSION —
 *     or a dangling imported handle) → `undefined` = DROP (mirrors the
 *     MLEADERSTYLE 341 drop-on-unresolved-name; never ship a dangling non-hex 342).
 *
 * `handleToName` / `nameToHandle` are built from the EMITTED BLOCK_RECORD table,
 * HEX handles only (a non-hex record handle is not a valid 342 target — same rule
 * as the DIMENSION F1 map build); `nameToRecord` additionally carries the handle-
 * less / non-hex records so the F2 mint can reach them.
 */
function resolveMleaderEntityArrowheadForExport(
  value: string,
  handleToName: Map<string, string>,
  nameToHandle: Map<string, string>,
  nameToRecord: ReadonlyMap<string, DxfBlockRecordEntry>,
  mintHandle: () => string,
): string | undefined {
  // F1 (round-9) — the DEFAULT closed-filled arrow normalizes to the null handle
  // `"0"`, never the literal `.` / `_CLOSEDFILLED` (a non-hex string the writer would
  // emit into the soft-pointer 342 → dangles on reopen).
  if (isDefaultArrowheadValue(value)) return DEFAULT_CLOSEDFILLED_HANDLE;
  const key = value.toLowerCase();
  if (handleToName.has(key)) return value; // imported live handle — verbatim
  const handle = nameToHandle.get(key); // authored NAME → its BLOCK_RECORD handle
  if (handle !== undefined) return handle;
  // F2 (round-9) — the NAME matches a BLOCK_RECORD present in the doc but carrying NO
  // hex handle yet (handle-less OR non-hex synthetic id). MINT a real hex handle ONTO
  // that existing record so the arrow resolves rather than being dropped (mirrors the
  // DIMENSION handle-less `dimblk*` mint). A genuinely unresolvable name (no matching
  // record at all) still drops.
  const record = nameToRecord.get(key);
  if (record !== undefined && !(record.handle && isHexHandle(record.handle))) {
    return mintHandleOntoRecord(record, key, nameToHandle, handleToName, mintHandle);
  }
  return undefined; // unresolvable → drop (writer omits 342)
}

/**
 * SL-11 round-8 F2 — export pass that rewrites every MLEADER ENTITY body-342
 * (`arrowheadBlock`) so a valid HANDLE (or nothing) reaches the writer, never an
 * authored block NAME the writer would emit verbatim and that dangles on reopen.
 *
 * Runs on BOTH the conformant and plain export paths (like
 * {@link stampDimensionDStyleArrowheadHandles}); it MUTATES the transport entities
 * in place. Covers top-level AND block-child MLEADERs (the orchestrator threads the
 * combined list). The MLEADER-specific rule (NO built-in-arrow synthesis, unlike
 * DIMENSION DSTYLE) matches `resolveMleaderArrowheadRef` + the reference audit's
 * `"entity"` site, so the three sites agree by construction.
 *
 * NOTE: the CONTEXT_DATA leader-line `arrowheadBlock` is NOT touched here — the
 * writer does not emit a per-leader-line body-342 (`mleaderContextWriter` emits no
 * 342 for a LEADER_LINE), so it never reaches the file from that field; the
 * conformant-remint follow (F3) still remaps a leader-line handle it DOES carry.
 *
 * `emittedHandles` / `handleSeedStart` power the SL-11 round-9 F2 mint: an arrow
 * BLOCK_RECORD an MLEADER names by NAME but that carries no hex handle yet (the
 * from-scratch / non-conformant path) gets a doc-wide collision-free hex handle
 * minted ONTO it (folded into `emittedHandles`) so the reference is not dropped.
 * Runs BEFORE {@link stampDimensionDStyleArrowheadHandles}, which re-derives its
 * seed from the SAME `emittedHandles` max — so a handle minted here is accounted
 * for and can never collide with a DIMENSION-synthesized arrow record.
 */
export function stampMleaderEntityArrowheadHandles(
  entities: DxfEntity[],
  blockRecords: DxfBlockRecordEntry[],
  emittedHandles: Set<string>,
  handleSeedStart: number,
): void {
  const handleToName = new Map<string, string>();
  const nameToHandle = new Map<string, string>();
  // F2 — name → the actual BLOCK_RECORD object, INCLUDING records that carry NO
  // group-5 handle yet (or a non-hex synthetic id) on the from-scratch /
  // non-conformant path. When an MLEADER names such a record we MINT a hex handle
  // ONTO it (below) instead of dropping the arrow. HEX-handle records additionally
  // populate the handle/name maps for the imported-handle + authored-name paths.
  const nameToRecord = new Map<string, DxfBlockRecordEntry>();
  for (const rec of blockRecords) {
    if (!rec.name) continue;
    if (!nameToRecord.has(rec.name.toLowerCase())) {
      nameToRecord.set(rec.name.toLowerCase(), rec);
    }
    if (!rec.handle || !isHexHandle(rec.handle)) continue;
    handleToName.set(rec.handle.toLowerCase(), rec.name);
    if (!nameToHandle.has(rec.name.toLowerCase())) {
      nameToHandle.set(rec.name.toLowerCase(), rec.handle);
    }
  }
  const mintHandle = makeHandleMinter(emittedHandles, handleSeedStart);
  for (const entity of entities) {
    if (entity.kind !== "supported-entity" || entity.type !== "MLEADER") {
      continue;
    }
    const geometry = entity.geometry;
    const current = geometry.arrowheadBlock;
    if (current === undefined || current === "") continue; // unset → writer omits 342
    const resolved = resolveMleaderEntityArrowheadForExport(
      current,
      handleToName,
      nameToHandle,
      nameToRecord,
      mintHandle,
    );
    // Unresolvable → clear the field so the writer omits body-342 (never a dangling
    // non-hex 342); otherwise write the resolved handle / verbatim default.
    geometry.arrowheadBlock = resolved ?? "";
  }
}
